import React, { ReactElement, useEffect, useRef, useState } from 'react'

export interface SparklinePoint {
  /** Bucket timestamp (start of day, ms). */
  x: number
  /** Volume for the bucket in the org default currency. */
  y: number
}

interface MoneySparklineProps {
  points: SparklinePoint[]
  height?: number
  /** Screen-reader description of what the line shows. */
  ariaLabel: string
}

/**
 * Compact, axis-less sparkline for the "Last 7 days" pulse card. The shared
 * LineChart component (Home's payment pulse) always reserves axis gutters and
 * renders tick labels, so this mirrors its line-plus-area idiom at sparkline
 * scale instead: ink stroke, faint fill, a dot on the latest point, no axes.
 * An all-zero week draws the flat baseline rather than an empty box.
 */
const MoneySparkline = ({ points, height = 64, ariaLabel }: MoneySparklineProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(280)

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }
    const measure = (): void => setWidth(element.getBoundingClientRect().width)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const pad = { top: 6, right: 7, bottom: 6, left: 3 }
  const plotWidth = Math.max(width - pad.left - pad.right, 1)
  const plotHeight = Math.max(height - pad.top - pad.bottom, 1)
  const maxValue = points.reduce((max, point) => Math.max(max, point.y), 0)

  const coords = points.map((point, index) => ({
    x: pad.left + (points.length > 1 ? (index / (points.length - 1)) * plotWidth : plotWidth / 2),
    // With no volume at all every point sits on the baseline: a flat line.
    y: pad.top + (maxValue > 0 ? (1 - point.y / maxValue) * plotHeight : plotHeight)
  }))

  const linePath = coords
    .map((coord, index) => `${index === 0 ? 'M' : 'L'} ${coord.x.toFixed(2)} ${coord.y.toFixed(2)}`)
    .join(' ')

  const baseline = (pad.top + plotHeight).toFixed(2)
  const areaPath = coords.length > 1
    ? `${linePath} L ${coords[coords.length - 1].x.toFixed(2)} ${baseline} L ${coords[0].x.toFixed(2)} ${baseline} Z`
    : ''

  const lastCoord = coords.length > 0 ? coords[coords.length - 1] : undefined

  return <div ref={containerRef} style={{ height, minWidth: 0 }}>
    <svg width='100%' height={height} role='img' aria-label={ariaLabel} style={{ display: 'block' }}>
      {areaPath && <path d={areaPath} fill='var(--sparkline-fill, rgba(32, 36, 45, 0.05))' />}
      {linePath && <path
        d={linePath}
        fill='none'
        stroke='var(--ink, #20242d)'
        strokeWidth={1.5}
        strokeLinecap='round'
        strokeLinejoin='round'
      />}
      {lastCoord &&
        <circle cx={lastCoord.x} cy={lastCoord.y} r={3} fill='var(--ink, #20242d)' stroke='var(--surface, #ffffff)' strokeWidth={1.5} />}
    </svg>
  </div>
}

export default MoneySparkline
